import { useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '@/components/primitives/Button.jsx';
import Input from '@/components/primitives/Input.jsx';
import ExecutionStatus from '@/components/surfaces/ExecutionStatus.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import connectApp from '@/redux/actions/pieveActions/connectApp.js';

const isFilled = (value) => String(value ?? '').trim() !== '';

/**
 * The form for apps that connect with typed values (an API key, a username and password, or the
 * app's own fields). Connecting saves the values and asks the app to accept them; the answer shows
 * under the button, in the app's own words when it says no.
 */
const ConnectForm = ({ app, method, onConnected }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState({});
  const [label, setLabel] = useState(app.name);
  const [phase, setPhase] = useState('idle');
  const [failure, setFailure] = useState('');
  const [credentialId, setCredentialId] = useState(null);
  const missingRequired = method.fields.some((field) => field.required && !isFilled(values[field.key]));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPhase('checking');
    setFailure('');
    const res = await dispatch(connectApp({ app, auth: method.auth, values, label, credentialId }));

    if (res?.credentialId) {
      setCredentialId(res.credentialId);
    }

    if (res?.success === false) {
      setPhase(res.stage === 'check' ? 'rejected' : 'idle');
      setFailure(res.message);
      return;
    }

    setPhase('idle');
    setValues({});
    setCredentialId(null);
    toastManager.add({ type: 'success', title: `${app.name} connected`, description: 'Pieve can now work in this account.' });
    onConnected?.();
  };

  return (
    <form className="connections-form" onSubmit={handleSubmit} noValidate>
      {method.fields.map((field) => (
        <Input key={field.key} label={field.name || field.key} hint={field.description || undefined} placeholder={field.placeholder || undefined} type={field.isSensitive ? 'password' : 'text'} autoComplete="off" mono={field.isSensitive === true} required={field.required === true} value={values[field.key] ?? ''} onChange={(event) => setValues((current) => ({ ...current, [field.key]: event.target.value }))} />
      ))}
      <Input label="Connection name" hint="Helps you tell accounts apart if you connect more than one." value={label} onChange={(event) => setLabel(event.target.value)} />
      <div className="connections-form__submit">
        <Button type="submit" variant="primary" loading={phase === 'checking'} disabled={missingRequired}>
          {phase === 'rejected' ? 'Try again' : `Connect ${app.name}`}
        </Button>
        {phase === 'checking' && <ExecutionStatus state="running" label={`Checking with ${app.name}`} size="sm" />}
      </div>
      {phase === 'rejected' && (
        <div className="connections-result" role="alert">
          <ExecutionStatus state="failed" label={`${app.name} did not accept these details`} size="sm" />
          <p className="connections-result__reason">{failure}</p>
          <p className="connections-result__next">The connection is saved as not working. Fix the values and try again, or remove it from your connections above.</p>
        </div>
      )}
      {phase === 'idle' && failure && (
        <p className="connections-error" role="alert">
          {failure}
        </p>
      )}
    </form>
  );
};

export default ConnectForm;
